import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { useCompany } from '@/contexts/CompanyContext';

export interface ApiConfig {
  apiBaseUrl: string;
  getHeaders: () => Promise<HeadersInit>;
  authenticatedFetch: <T>(
    path: string,
    options?: RequestInit
  ) => Promise<T>;
}

const ApiConfigContext = createContext<ApiConfig | null>(null);

export function useApiConfig(): ApiConfig {
  const context = useContext(ApiConfigContext);
  if (!context) {
    throw new Error('useApiConfig must be used within an ApiConfigProvider');
  }
  return context;
}

interface ApiConfigProviderProps {
  apiBaseUrl: string;
  children: ReactNode;
}

export function ApiConfigProvider({ apiBaseUrl, children }: ApiConfigProviderProps): React.ReactElement {
  const { getAccessToken, instanceId } = useAuth();
  const { selectedCompany } = useCompany();

  const getHeaders = useCallback(async (): Promise<HeadersInit> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add Bearer token
    if (getAccessToken) {
      try {
        const token = await getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Failed to get access token:', error);
      }
    }

    // Add Instance ID header
    if (instanceId) {
      headers['X-Instance-Id'] = instanceId;
    }

    // Add Company ID header (selected company)
    if (selectedCompany?.id) {
      headers['X-Company-Id'] = selectedCompany.id;
    }

    return headers;
  }, [getAccessToken, instanceId, selectedCompany?.id]);

  const authenticatedFetch = useCallback(async <T,>(
    path: string,
    options?: RequestInit
  ): Promise<T> => {
    const url = `${apiBaseUrl}${path}`;
    const headers = await getHeaders();

    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP error ${response.status}`,
        response.status,
        errorData
      );
    }

    return await response.json();
  }, [apiBaseUrl, getHeaders]);

  const value = useMemo<ApiConfig>(() => ({
    apiBaseUrl,
    getHeaders,
    authenticatedFetch,
  }), [apiBaseUrl, getHeaders, authenticatedFetch]);

  return (
    <ApiConfigContext.Provider value={value}>
      {children}
    </ApiConfigContext.Provider>
  );
}

export class ApiError extends Error {
  statusCode?: number;
  response?: unknown;
  constructor(
    message: string,
    statusCode?: number,
    response?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.response = response;
  }
}
